/**
 * Mirrored from `cosmo-bud-portal-backend/src/modules/tutors/dto/tutor-profile.schemas.ts`.
 * Drift is enforced by `scripts/diff-schemas.mjs` in CI — never edit one side
 * without the other.
 *
 * Constants are duplicated too (also kept in sync by the diff script). They
 * mirror `cosmo-bud-portal-backend/src/modules/tutors/constants.ts`, which in
 * turn mirrors `cosmo-bud-forms-backend/backend/validation/constants.ts`.
 */
import { z } from 'zod';

export const EDUCATOR_SUBJECTS = [
  'Mathematics',
  'Science (Physics)',
  'Science (Chemistry)',
  'Science (Biology)',
  'English Communication',
  'Exam Prep',
  'AI / Computer Science',
] as const;
export type EducatorSubject = (typeof EDUCATOR_SUBJECTS)[number];

export const GRADE_LEVELS = [
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
  'Grade 8',
  'Grade 9',
  'Grade 10',
  'Grade 11',
  'Grade 12',
  'College/University',
] as const;
export type GradeLevel = (typeof GRADE_LEVELS)[number];

export const QUALIFICATIONS = [
  "Bachelor's",
  "Master's",
  'PhD',
  'Teaching Certification (B.Ed/TEFL/etc.)',
  'Other',
] as const;
export type QualificationDegree = (typeof QUALIFICATIONS)[number];

export const ENGLISH_PROFICIENCY = [
  'native',
  'fluent',
  'professional',
  'conversational',
] as const;
export type EnglishProficiency = (typeof ENGLISH_PROFICIENCY)[number];

export const TUTOR_GENDERS = [
  'male',
  'female',
  'non_binary',
  'prefer_not_to_say',
] as const;
export type TutorGender = (typeof TUTOR_GENDERS)[number];

export const PAYOUT_CURRENCIES = ['INR', 'USD'] as const;
export type PayoutCurrency = (typeof PAYOUT_CURRENCIES)[number];

export const PUBLISH_STATUSES = [
  'draft',
  'pending_review',
  'published',
  'paused',
  'changes_requested',
] as const;
export type PublishStatus = (typeof PUBLISH_STATUSES)[number];

export const DAYS_OF_WEEK = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const LANGUAGE_CODES = [
  'en',
  'hi',
  'ta',
  'te',
  'kn',
  'ml',
  'mr',
  'bn',
  'gu',
  'pa',
  'ur',
  'fr',
  'es',
  'de',
  'ar',
  'zh',
] as const;
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const HHMM_OR_MIDNIGHT = /^([01]\d|2[0-3]):[0-5]\d$|^24:00$/;

const TimeStringSchema = z
  .string()
  .regex(HHMM, 'Expected HH:mm in 24-hour time');

const EndTimeStringSchema = z
  .string()
  .regex(HHMM_OR_MIDNIGHT, 'Expected HH:mm in 24-hour time (24:00 = end of day)');

export const QualificationSchema = z.object({
  degree: z.enum(QUALIFICATIONS),
  institution: z.string().trim().min(1).max(200),
  year: z.number().int().min(1950).max(2100),
  field: z.string().trim().max(200).nullable().optional().default(null),
});

export const AvailabilityRuleSchema = z
  .object({
    day: z.enum(DAYS_OF_WEEK),
    startLocal: TimeStringSchema,
    endLocal: EndTimeStringSchema,
  })
  .refine((r) => r.endLocal > r.startLocal, {
    path: ['endLocal'],
    message: 'endLocal must be later than startLocal',
  });
export type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;

export const GradeLevelsArraySchema = z.preprocess((val) => {
  if (!Array.isArray(val)) return val;
  const result: string[] = [];
  for (const item of val) {
    if (typeof item !== 'string') {
      result.push(item);
      continue;
    }
    const normalized = item.trim();
    if (
      normalized === 'Grades 9-10' ||
      normalized === 'Grade 9-10' ||
      normalized === '9-10' ||
      normalized === 'Grade 9-10 (CBSE/ICSE)'
    ) {
      result.push('Grade 9', 'Grade 10');
    } else if (
      normalized === 'Grades 11-12' ||
      normalized === 'Grade 11-12' ||
      normalized === 'Grades 11/12' ||
      normalized === '11-12'
    ) {
      result.push('Grade 11', 'Grade 12');
    } else if (normalized === 'Grades 6-8' || normalized === 'Grade 6-8' || normalized === '6-8') {
      result.push('Grade 6', 'Grade 7', 'Grade 8');
    } else if (normalized === 'Grades 3-5' || normalized === 'Grade 3-5' || normalized === '3-5') {
      result.push('Grade 3', 'Grade 4', 'Grade 5');
    } else if (normalized === 'College/Uni') {
      result.push('College/University');
    } else {
      result.push(item);
    }
  }
  const unique = Array.from(new Set(result));
  return unique.filter((item) => (GRADE_LEVELS as readonly string[]).includes(item));
}, z.array(z.enum(GRADE_LEVELS)));

export const HourlyRateSchema = z.object({
  usdMinor: z.number().int().min(0),
});

export const BankDetailsSchema = z.object({
  accountHolderName: z.string().trim().min(1).max(200),
  bankName: z.string().trim().min(1).max(200),
  accountNumber: z.string().trim().min(4).max(64),
  ifscOrSwift: z.string().trim().min(4).max(32),
  branchAddress: z.string().trim().max(500).nullable().optional().default(null),
  country: z.string().trim().min(1).max(80),
});
export type BankDetailsInput = z.infer<typeof BankDetailsSchema>;

export const CreateTutorProfileSchema = z.object({
  displayName: z.string().trim().max(120).optional(),
  headline: z.string().trim().max(160).optional(),
  bio: z.string().trim().max(1500).optional(),
  gender: z.enum(TUTOR_GENDERS).optional(),
  languagesSpoken: z.array(z.enum(LANGUAGE_CODES)).max(20).optional(),
  subjects: z.array(z.enum(EDUCATOR_SUBJECTS)).max(20).optional(),
  gradeLevels: z.array(z.enum(GRADE_LEVELS)).max(20).optional(),
  yearsExperience: z.number().int().min(0).max(50).optional(),
  qualifications: z.array(QualificationSchema).max(10).optional(),
  englishProficiency: z.enum(ENGLISH_PROFICIENCY).optional(),
  hourlyRate: HourlyRateSchema.optional(),
  weeklyAvailabilityHours: z.number().int().min(0).max(60).optional(),
  homeTz: z.string().trim().min(1).max(80).optional(),
  availability: z.array(AvailabilityRuleSchema).max(20).optional(),
  demoVideoUrl: z.string().trim().url().nullable().optional(),
  linkedinUrl: z
    .string()
    .trim()
    .url()
    .refine((u) => /linkedin\.com\//i.test(u), {
      message: 'linkedinUrl must be a linkedin.com URL',
    })
    .nullable()
    .optional(),
  payoutCurrency: z.enum(PAYOUT_CURRENCIES).optional(),
  bankDetails: BankDetailsSchema.partial().optional(),
  consentBackgroundCheck: z.boolean().optional(),
  completedSteps: z.array(z.string().min(1).max(40)).max(20).optional(),
  publishStatus: z.literal('pending_review').optional(),
});
export type CreateTutorProfileInput = z.infer<typeof CreateTutorProfileSchema>;

export const UpdateTutorProfileSchema = CreateTutorProfileSchema.partial();
export type UpdateTutorProfileInput = z.infer<typeof UpdateTutorProfileSchema>;

export const TutorProfileResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  displayName: z.string(),
  headline: z.string(),
  bio: z.string(),
  gender: z.enum(TUTOR_GENDERS),
  languagesSpoken: z.array(z.string()),
  subjects: z.array(z.enum(EDUCATOR_SUBJECTS)),
  gradeLevels: GradeLevelsArraySchema,
  yearsExperience: z.number().int(),
  qualifications: z.array(QualificationSchema),
  englishProficiency: z.enum(ENGLISH_PROFICIENCY),
  hourlyRate: HourlyRateSchema,
  weeklyAvailabilityHours: z.number().int(),
  homeTz: z.string(),
  availability: z.array(AvailabilityRuleSchema),
  photoUrl: z.string().nullable(),
  cvUrl: z.string().nullable(),
  demoVideoUrl: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
  payoutCurrency: z.enum(PAYOUT_CURRENCIES),
  consentBackgroundCheck: z.boolean(),
  profileCompletion: z.number().int().min(0).max(100),
  eligibleForInvites: z.boolean(),
  publishStatus: z.enum(PUBLISH_STATUSES),
  completedSteps: z.array(z.string()),
  bankDetailsOnFile: z.boolean(),
  changeRequestNote: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TutorProfileResponse = z.infer<typeof TutorProfileResponseSchema>;

export const OnboardingStatusSchema = z.object({
  requiredSteps: z.array(z.string()),
  completedSteps: z.array(z.string()),
  missingSteps: z.array(z.string()),
  canPublish: z.boolean(),
  profileCompletion: z.number().int().min(0).max(100),
});
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

export const PhotoUploadRequestSchema = z.object({
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  contentLength: z.number().int().min(1).max(5 * 1024 * 1024),
});
export type PhotoUploadRequestInput = z.infer<typeof PhotoUploadRequestSchema>;

export const PhotoUploadResponseSchema = z.object({
  uploadUrl: z.string().url(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  objectPath: z.string(),
  publicUrl: z.string().url(),
  expiresAt: z.string(),
});
export type PhotoUploadResponse = z.infer<typeof PhotoUploadResponseSchema>;

export const PhotoConfirmSchema = z.object({
  objectPath: z.string().min(1),
});

export const TutorSearchQuerySchema = z.object({
  name: z.string().trim().max(120).optional(),
  subject: z.enum(EDUCATOR_SUBJECTS).optional(),
  gradeLevel: z.enum(GRADE_LEVELS).optional(),
  language: z.enum(LANGUAGE_CODES).optional(),
  gender: z.enum(TUTOR_GENDERS).optional(),
  minHourlyInr: z.coerce.number().int().min(0).optional(),
  maxHourlyInr: z.coerce.number().int().min(0).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export type TutorSearchQuery = z.infer<typeof TutorSearchQuerySchema>;

export const PublicTutorCardSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  headline: z.string(),
  subjects: z.array(z.enum(EDUCATOR_SUBJECTS)),
  gradeLevels: GradeLevelsArraySchema,
  languagesSpoken: z.array(z.string()),
  gender: z.enum(TUTOR_GENDERS),
  hourlyRate: HourlyRateSchema,
  photoUrl: z.string().nullable(),
  homeTz: z.string(),
  weeklyAvailabilityHours: z.number().int(),
  availability: z.array(AvailabilityRuleSchema),
  averageRating: z.number().nullable(),
});
export type PublicTutorCard = z.infer<typeof PublicTutorCardSchema>;

export const PublicTutorViewSchema = PublicTutorCardSchema.extend({
  bio: z.string(),
  yearsExperience: z.number().int(),
  qualifications: z.array(QualificationSchema),
  englishProficiency: z.enum(ENGLISH_PROFICIENCY),
  demoVideoUrl: z.string().nullable(),
  createdAt: z.string(),
});
export type PublicTutorView = z.infer<typeof PublicTutorViewSchema>;

export const TutorSearchResponseSchema = z.object({
  results: z.array(PublicTutorCardSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type TutorSearchResponse = z.infer<typeof TutorSearchResponseSchema>;
export type PhotoConfirmInput = z.infer<typeof PhotoConfirmSchema>;
